// src/components/RankingsMap.jsx — s136, Phase 5: the map on /water/rankings.
// Geometry from src/data/rankingsMap.js (generated, gated by verify-rankings-map). Every colour and
// figure comes from waterRankings.js — the same rows the tables print — so the map cannot say
// anything the tables don't. A filled dot is a city whose utility publishes a hardness figure,
// coloured by the band of its ranking key; a hollow dot is a covered city whose utility publishes
// none (never coloured, never guessed). Lazy-loaded by WaterRankings.jsx so the tables load first.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RANKINGS_MAP as M, MAP_NATION, MAP_BORDERS } from '../data/rankingsMap.js';
import { WATER_RANKINGS as R } from '../data/waterRankings.js';
import { classifyHardness, HARDNESS_BANDS } from '../data/water.js';

// Five steps, sage → deep sienna, readable on both themes. Order follows HARDNESS_BANDS.
export const BAND_FILL = { soft: '#8FA58C', moderate: '#C8B47A', hard: '#D38A4A', 'very-hard': '#B5532A', 'extremely-hard': '#6E2A14' };

// s136 — three layers, each using the groups the site already prints: hardness bands (water.js),
// the TDS flags of the city water panel (waterPanel.js: ≤100 often dries clean, ≥300 spots on
// dry-down), and the disinfectant each utility reports (chloramine vs free chlorine).
const CHLORAMINE = /chloramine|seasonal switch/;
export const LAYERS = {
  hardness: { label: 'HARDNESS', noun: 'hardness figure',
    groups: HARDNESS_BANDS.map((b) => ({ key: b.key, label: b.label.toUpperCase(), fill: BAND_FILL[b.key] })),
    group: (k, ctx) => { const r = ctx.H.get(k); return r ? classifyHardness(r.v)?.key : null; },
    say: (k, ctx) => { const r = ctx.H.get(k); return r ? `${r.fig} mg/L hardness published by ${r.by}; rank ${r.rank} of ${R.hardness.length}.` : null; } },
  tds: { label: 'DISSOLVED SOLIDS', noun: 'dissolved-solids figure',
    groups: [{ key: 'low', label: '100 OR LESS · OFTEN DRIES CLEAN', fill: '#8FA58C' }, { key: 'mid', label: '101–299', fill: '#C8B47A' }, { key: 'high', label: '300+ · SPOTS ON DRY-DOWN', fill: '#8E3A1C' }],
    group: (k, ctx) => { const r = ctx.T.get(k); if (!r) return null; return r.flag === 'Spots on dry-down' ? 'high' : r.flag === 'Often dries clean' ? 'low' : 'mid'; },
    say: (k, ctx) => { const r = ctx.T.get(k); return r ? `${r.fig} mg/L dissolved solids published by ${r.by}; rank ${r.rank} of ${R.tds.length}.` : null; } },
  disinfectant: { label: 'CHLORAMINE', noun: 'disinfectant',
    groups: [{ key: 'chloramine', label: 'CHLORAMINE', fill: '#B5532A' }, { key: 'chlorine', label: 'FREE CHLORINE', fill: '#8FA58C' }],
    group: (k) => { const t = R.disinfectantBy[k]; if (!t || t === 'not published') return null; return CHLORAMINE.test(t) ? 'chloramine' : 'chlorine'; },
    say: (k) => { const t = R.disinfectantBy[k]; return t && t !== 'not published' ? `the utility reports ${t}.` : null; } },
};

export default function RankingsMap({ T, fontMono, fontBody }) {
  const [sel, setSel] = useState(null);
  // Screen px per map unit: dots keep ~5 px on screen at any width (≈2 px on a phone otherwise),
  // and a tap picks the NEAREST city within 22 screen px, so a thumb doesn't have to land on a dot.
  const svgRef = useRef(null);
  const [k, setK] = useState(1);
  useEffect(() => {
    const el = svgRef.current; if (!el) return undefined;
    const upd = () => setK(el.getBoundingClientRect().width / M.w || 1);
    upd(); const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(upd) : null; ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const rDot = Math.max(6, 5 / k), rSel = rDot * 1.6;
  const [layer, setLayer] = useState('hardness');
  const L = LAYERS[layer];
  const ctx = useMemo(() => ({ H: new Map(R.hardness.map((r) => [`${r.s}/${r.u}`, r])), T: new Map(R.tds.map((r) => [`${r.s}/${r.u}`, r])) }), []);
  const { dots, counts } = useMemo(() => {
    const counts = Object.fromEntries(L.groups.map((g) => [g.key, 0])); counts.none = 0;
    const fillOf = Object.fromEntries(L.groups.map((g) => [g.key, g.fill]));
    const dots = M.dots.map(([k, x, y]) => { const g = L.group(k, ctx); if (g) counts[g]++; else counts.none++; return { k, x, y, g, fill: g ? fillOf[g] : null }; });
    dots.sort((a, b) => (a.g ? 1 : 0) - (b.g ? 1 : 0)); // hollow first, filled on top
    return { dots, counts };
  }, [layer]);
  const other = useMemo(() => {
    const m = new Map(); for (const [kind, rows] of Object.entries(R.hardnessNotRanked)) for (const x of rows) m.set(`${x.s}/${x.u}`, { ...x, kind });
    return m;
  }, []);
  const s = sel ? dots.find((d) => d.k === sel) : null;
  const cityLabel = (d) => { const x = ctx.H.get(d.k) || other.get(d.k); return `${x.c}, ${x.st}`; };
  const pick = (d) => setSel(d.k);

  return (
    <figure data-region="rank-map" data-layer={layer} style={{ margin: '0 0 12px' }}>
      <div role="group" aria-label="Map layer" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {Object.entries(LAYERS).map(([key, l]) => (
          <button key={key} type="button" data-layer-btn={key} aria-pressed={layer === key} onClick={() => setLayer(key)} style={{
            background: 'transparent', border: `1px solid ${layer === key ? T.sienna : T.rule}`, color: layer === key ? T.sienna : T.inkFaint,
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.1em', padding: '6px 10px', borderRadius: 2, cursor: 'pointer',
          }}>{l.label}</button>
        ))}
      </div>
      <svg viewBox={`0 0 ${M.w} ${M.h}`} role="group" aria-label={`Map of the ${M.dots.length} covered cities, ${L.label.toLowerCase()} layer: ${M.dots.length - counts.none} coloured from their utility's published ${L.noun}, ${counts.none} hollow where the utility publishes none. The same data is in the tables below.`}
        ref={svgRef} onPointerUp={(e) => {
          const ctm = svgRef.current?.getScreenCTM(); if (!ctm) return;
          const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
          let best = null, bd = Infinity; for (const d of dots) { const dd = Math.hypot(d.x - p.x, d.y - p.y); if (dd < bd) { bd = dd; best = d; } }
          if (best && bd * k <= 22) pick(best); else setSel(null);
        }}
        style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'manipulation' }}>
        <path d={MAP_NATION} fill={T.surface} stroke={T.inkFaint} strokeWidth="0.8" />
        <path d={MAP_BORDERS} fill="none" stroke={T.rule} strokeWidth="0.7" />
        {dots.map((d) => (
          <circle key={d.k} data-k={d.k} cx={d.x} cy={d.y} r={d.k === sel ? rSel : rDot}
            fill={d.fill || T.bg} stroke={d.g ? (d.k === sel ? T.ink : T.bg) : T.inkFaint} data-g={d.g || 'none'}
            strokeWidth={(d.k === sel ? 2 : d.g ? 1 : 1.4) / Math.min(1, k)}
            tabIndex={0} role="button" aria-label={`${cityLabel(d)}: ${L.say(d.k, ctx) || `no ${L.noun} published`}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(d); } }}
            onMouseEnter={() => setSel(d.k)} style={{ cursor: 'pointer', outline: 'none' }}>
            <title>{`${cityLabel(d)} — ${L.say(d.k, ctx) || 'not published'}`}</title>
          </circle>
        ))}
      </svg>
      <div aria-live="polite" data-region="rank-map-pick" style={{ minHeight: 44, padding: '8px 0', borderBottom: `1px solid ${T.rule}`, fontFamily: fontBody, fontSize: 14, color: T.inkSoft }}>
        {s ? (
          <>
            <Link to={`/states/${s.k}`} style={{ color: T.sienna }}>{cityLabel(s)}</Link>
            {L.say(s.k, ctx)
              ? <> — {L.say(s.k, ctx)}</>
              : layer === 'hardness' && other.get(s.k)?.kind === 'qualitative'
                ? <> — the utility describes its water in words{other.get(s.k).word ? ` (“${other.get(s.k).word}”)` : ''} but prints no figure, so the city is not ranked.</>
                : layer === 'hardness' && other.get(s.k)?.kind === 'unlocated'
                  ? <> — we have not yet found the utility's hardness figure, so the city is not ranked.</>
                  : <> — the utility publishes no {L.noun}{layer === 'disinfectant' ? '' : ', so the city is not ranked'}.</>}
          </>
        ) : <span style={{ color: T.inkFaint }}>Tap or hover a dot for the city's published figure.</span>}
      </div>
      <figcaption style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 10, fontFamily: fontMono, fontSize: 10, letterSpacing: '0.08em', color: T.inkFaint }}>
        {L.groups.map((g) => (
          <span key={g.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <svg width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="4.5" fill={g.fill} /></svg>
            {g.label} · {counts[g.key]}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <svg width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="4" fill="none" stroke={T.inkFaint} strokeWidth="1.4" /></svg>
          NOT PUBLISHED · {counts.none}
        </span>
      </figcaption>
    </figure>
  );
}

